import {FC, useContext, useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
	getAuction,
	getAuctionCategoriesSelector,
	getAuctionLoaderSelector,
	getAuctionSelector,
} from '@/store/organization-auction';
import {Box, Typography} from '@mui/material';
import {
	BID_INCREMENT,
	BODY1,
	BODY2,
	BUY_NOW_PRICE,
	CANCEL,
	DONORS,
	EDIT,
	EDIT_ITEM,
	H6,
	IMAGE_PREVIEW_ALT,
	MIN_BID,
	SUBTITLE1,
	VALUE,
	WINNER,
	WINNING_BID,
} from '@/utils/constants';
import TableFooterActions from '@/shared/components/Table/TableFooterActions';
import {AuctionDetailsContext} from '../AuctionDetailsContext';
import {AUCTION_TYPE} from '@/utils/enums';
import {useSelector} from 'react-redux';
import {auctionDetailsItemStyle} from './AuctionDetailsItem.styles';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import AuctionDetailsDonor from '../AuctionDetailsDonor';
import Modal from '@/shared/components/Modal';
import {auctionTableStyle} from '../../AuctionTable.styles';
import AuctionItem from '../../AuctionItem';
import Loader from '@/shared/components/Loader';
import {currencyFormatter} from '@/utils/helpers/currency';
import {orgAPI} from '@/core/api';

const AuctionDetailsItem: FC = () => {
	const {editableAuction, onCloseModal} = useContext(AuctionDetailsContext);
	const [isShowAuctionModal, setIsShowAuctionModal] = useState<boolean>(false);
	const auctionData = useSelector(getAuctionSelector);
	const isLoading = useSelector(getAuctionLoaderSelector);

	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!editableAuction?.id) return;
		const getAuctionInfo = async () => {
			await dispatch(getAuction({id: editableAuction?.id, apiInstance: orgAPI})).unwrap();
		};

		getAuctionInfo();
	}, [isShowAuctionModal]);

	const auctionCategories = useAppSelector(getAuctionCategoriesSelector);

	const [isDonorModalOpen, setIsDonorModalOpen] = useState(false);

	const categoryName = useMemo(
		() => auctionCategories?.find((auctionCategory) => auctionCategory.id === auctionData?.categoryId)?.name,
		[auctionCategories, auctionData]
	);

	const liveAuctionTitle = auctionData?.isLiveAuction ? AUCTION_TYPE.LIVE : AUCTION_TYPE.SILENT;

	const handleChangeDonorModal = () => setIsDonorModalOpen(!isDonorModalOpen);

	return (
		<Box>
			<Box sx={auctionDetailsItemStyle.wrapper}>
				{isLoading ? (
					<Loader parentClassName={auctionTableStyle.loader} size={50} />
				) : (
					<>
						<Box sx={auctionDetailsItemStyle.logosWrapper}>
							<Box sx={auctionDetailsItemStyle.content}>
								{auctionData?.itemImages.length ? (
									auctionData?.itemImages.map((item, index) => (
										<Box
											key={item.image + index}
											sx={{
												...auctionDetailsItemStyle.donorLogo,
												'&:first-of-type:before': {
													content: `"${liveAuctionTitle}"`,
													position: 'absolute',
													top: '8px',
													right: '8px',
													backgroundColor: '#fff',
													color: 'var(--primary-500)',
													borderRadius: '50px',
													padding: '4px 12px',
													fontSize: '12px',
												},
											}}
										>
											<img src={item.image} alt={IMAGE_PREVIEW_ALT} />
										</Box>
									))
								) : (
									<Box sx={auctionDetailsItemStyle.altContent}>
										<JudgeIcon />
									</Box>
								)}
							</Box>
							<Box>
								<Box sx={auctionDetailsItemStyle.title}>
									<Typography variant={H6}>{auctionData?.name}</Typography>
									<Typography variant={BODY1} sx={auctionDetailsItemStyle.category}>
										{categoryName}
									</Typography>
								</Box>

								<Box sx={auctionDetailsItemStyle.valuesWrapper}>
									<Box sx={auctionDetailsItemStyle.valuesTitles}>
										<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
											{VALUE}:
										</Typography>
										<Typography variant={BODY2}>{currencyFormatter.format(auctionData?.value ?? 0)}</Typography>
									</Box>
									<Box sx={auctionDetailsItemStyle.valuesTitles}>
										<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
											{MIN_BID}:
										</Typography>
										<Typography variant={BODY2}>{currencyFormatter.format(auctionData?.minBid ?? 0)}</Typography>
									</Box>
									<Box sx={auctionDetailsItemStyle.valuesTitles}>
										<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
											{BID_INCREMENT}:
										</Typography>
										<Typography variant={BODY2}>{currencyFormatter.format(auctionData?.bidIncrement ?? 0)}</Typography>
									</Box>
									<Box sx={auctionDetailsItemStyle.valuesTitles}>
										<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
											{BUY_NOW_PRICE}:
										</Typography>
										<Typography variant={BODY2}>{currencyFormatter.format(auctionData?.buyNowPrice ?? 0)}</Typography>
									</Box>
								</Box>

								<Box sx={auctionDetailsItemStyle.winnerWrapper}>
									{auctionData?.winningBid && (
										<Box sx={auctionDetailsItemStyle.valuesTitles}>
											<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
												{WINNING_BID}:
											</Typography>
											<Typography variant={BODY2}>{currencyFormatter.format(auctionData?.winningBid ?? 0)}</Typography>
										</Box>
									)}
									{auctionData?.winnerId && (
										<Box sx={auctionDetailsItemStyle.valuesTitles}>
											<Typography variant={BODY2} sx={auctionDetailsItemStyle.valuesTitle}>
												{WINNER}:
											</Typography>
											<Typography variant={BODY2}>
												{auctionData?.winnerDetails?.firstName} {auctionData?.winnerDetails?.lastName}
											</Typography>
										</Box>
									)}
								</Box>
								<Box />
							</Box>
						</Box>

						<Box sx={auctionDetailsItemStyle.description}>
							<Typography variant={SUBTITLE1}>Item Description</Typography>
							<Typography sx={auctionDetailsItemStyle.descriptionText} variant={BODY1}>
								{auctionData?.description}
							</Typography>
						</Box>

						{!!auctionData?.donors.length && (
							<Box sx={auctionDetailsItemStyle.donorsWrapper}>
								<Typography variant={SUBTITLE1}>{DONORS}</Typography>
								<AuctionDetailsDonor
									isModalOpen={isDonorModalOpen}
									onModalChange={handleChangeDonorModal}
									donors={auctionData && auctionData?.donors}
									id={auctionData?.id}
								/>
							</Box>
						)}
					</>
				)}
				<TableFooterActions
					titleButtonPrev={CANCEL}
					titleButtonNext={EDIT}
					onClickButtonNext={() => setIsShowAuctionModal(true)}
					onClickButtonPrev={onCloseModal}
				/>
			</Box>
			<Modal
				onClose={() => setIsShowAuctionModal(false)}
				open={isShowAuctionModal}
				sx={auctionTableStyle.auctionModal}
				title={EDIT_ITEM}
			>
				<AuctionItem onCloseModal={() => setIsShowAuctionModal(false)} editableAuction={auctionData} />
			</Modal>
		</Box>
	);
};
export default AuctionDetailsItem;
